// The landing page's particle field as a standalone full-bleed backdrop,
// holding one shape (`morph`: 0 wave … 2 "@", 3 envelope) instead of being
// scroll-driven. Used by the login page so it speaks the same visual language.
import React, { useEffect, useRef, useState } from 'react';
import { InkField, isWebGLAvailable } from './inkField';
import './shared.css';

export function InkBackdrop({ morph = 2, className = '', portrait }) {
  const wrapRef = useRef(null);
  const fieldRef = useRef(null);
  const morphRef = useRef(morph);
  const [failed, setFailed] = useState(false);

  morphRef.current = morph;

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    // `portrait` is read once at mount (it's layout config, not state).
    // Per-mount canvas, same reason as InkStage (StrictMode remounts).
    const canvas = document.createElement('canvas');
    canvas.className = 'lp-canvas';
    wrap.prepend(canvas);
    let field = null;
    if (isWebGLAvailable()) {
      try {
        field = new InkField(canvas, {
          reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
          ...(portrait ? { portrait } : {}),
        });
      } catch (e) {
        field = null;
      }
    }
    if (!field) {
      setFailed(true);
      return () => canvas.remove();
    }
    fieldRef.current = field;
    field.setMorph(morphRef.current);
    const onPointer = (e) => field.setPointer((e.clientX / window.innerWidth) * 2 - 1, -((e.clientY / window.innerHeight) * 2 - 1));
    const onResize = () => field.resize();
    const sync = () => (document.hidden ? field.stop() : field.start());
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', sync);
    sync();
    return () => {
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', sync);
      field.destroy();
      fieldRef.current = null;
      canvas.remove();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fieldRef.current) fieldRef.current.setMorph(morph);
  }, [morph]);

  return (
    <div ref={wrapRef} className={`lp-canvas-wrap ${className}`} aria-hidden="true">
      {failed && <div className="lp-canvas-fallback" />}
    </div>
  );
}

export default InkBackdrop;
